// token-alert-v1: card แสดง Token Health ใน Super Admin > tab "⚙️ ระบบ"
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const SA = axios.create({ baseURL: window.location.origin });

const STATUS_STYLE = {
  healthy:      { bg: '#d1fae5', fg: '#065f46', label: '✅ ปกติ' },
  invalid:      { bg: '#fee2e2', fg: '#991b1b', label: '🚨 Token พัง' },
  rate_limited: { bg: '#fef3c7', fg: '#92400e', label: '⏳ Rate limited' },
  unknown:      { bg: '#e2e8f0', fg: '#334155', label: '❓ ไม่ทราบ' },
  expired:      { bg: '#fee2e2', fg: '#991b1b', label: '⏰ หมดอายุ' },
};

export default function TokenHealthCard({ token, isMobile }) {
  const [health, setHealth] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  async function loadAll() {
    setLoading(true);
    try {
      const [hr, ar] = await Promise.all([
        SA.get('/api/token-health', auth),
        SA.get('/api/token-health/alerts?unackOnly=false&limit=50', auth),
      ]);
      if (hr.data?.success) setHealth(hr.data.data || []);
      if (ar.data?.success) setAlerts(ar.data.data || []);
    } catch (e) {
      console.error('TokenHealthCard load', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  async function manualCheck(id) {
    setBusy(`check-${id}`);
    try {
      const { data } = await SA.post(`/api/token-health/${id}/check`, {}, auth);
      if (data.success) {
        if (data.data.ok) alert(`✅ Token ใช้งานได้ (HTTP ${data.data.status})`);
        else alert(`❌ Token ใช้ไม่ได้ (HTTP ${data.data.status})\n${JSON.stringify(data.data.error)}`);
      } else {
        alert(`Error: ${data.error}`);
      }
      await loadAll();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setBusy(null);
    }
  }

  async function ackAll(id) {
    setBusy(`ack-${id}`);
    try {
      await SA.post(`/api/token-health/${id}/ack-all`, {}, auth);
      await loadAll();
    } finally {
      setBusy(null);
    }
  }

  const unackCount = alerts.filter(a => !a.acknowledged).length;
  const invalidCount = health.filter(h => h.status === 'invalid').length;

  return (
    <div style={{
      background: '#1e293b', borderRadius: 14, padding: isMobile ? 16 : 24,
      marginBottom: 24, color: '#f1f5f9',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 600 }}>🔐 Token Health</h2>
        {invalidCount > 0 && (
          <span style={{
            background: '#dc2626', color: '#fff', borderRadius: 12,
            padding: '2px 10px', fontSize: 11, fontWeight: 700,
          }}>{invalidCount} พัง</span>
        )}
        {unackCount > 0 && (
          <span style={{
            background: '#f59e0b', color: '#fff', borderRadius: 12,
            padding: '2px 10px', fontSize: 11, fontWeight: 700,
          }}>{unackCount} แจ้งเตือน</span>
        )}
        <button onClick={loadAll}
          disabled={loading}
          style={{
            marginLeft: 'auto', background: '#0ea5e9', color: '#fff',
            border: 'none', borderRadius: 6, padding: '5px 12px',
            fontSize: 12, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
          }}>
          {loading ? 'กำลังโหลด...' : '🔄 รีเฟรช'}
        </button>
      </div>
      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 16px' }}>
        ติดตามสถานะ token ของ external services (LINE, Slip2Go) ระบบตรวจอัตโนมัติทุก 1 ชม.
      </p>

      {/* Health Table */}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#0f172a', color: '#94a3b8', textAlign: 'left' }}>
            <th style={th}>Service</th>
            {!isMobile && <th style={th}>Token Ref</th>}
            <th style={th}>Tenant</th>
            <th style={th}>สถานะ</th>
            {!isMobile && <th style={th}>ตรวจล่าสุด</th>}
            <th style={th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {health.map((h) => {
            const s = STATUS_STYLE[h.status] || STATUS_STYLE.unknown;
            return (
              <tr key={h.id} style={{ borderTop: '1px solid #0f172a' }}>
                <td style={td}><strong>{h.service}</strong></td>
                {!isMobile && (
                  <td style={td}>
                    <code style={{ fontSize: 11, color: '#64748b' }}>
                      {h.token_ref === 'global' ? '(global)' : (h.token_ref || '').substring(0, 12) + '…'}
                    </code>
                  </td>
                )}
                <td style={td}>{h.tenant_name || '-'}</td>
                <td style={td}>
                  <span style={{
                    background: s.bg, color: s.fg, padding: '3px 8px',
                    borderRadius: 4, fontSize: 11, fontWeight: 600,
                  }}>{s.label}</span>
                </td>
                {!isMobile && (
                  <td style={{ ...td, fontSize: 11, color: '#64748b' }}>
                    {h.last_checked_at
                      ? new Date(h.last_checked_at).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })
                      : '-'}
                  </td>
                )}
                <td style={td}>
                  <button
                    onClick={() => manualCheck(h.id)}
                    disabled={busy === `check-${h.id}`}
                    style={btnSmall}
                  >
                    {busy === `check-${h.id}` ? '…' : 'ทดสอบ'}
                  </button>
                  {' '}
                  <button
                    onClick={() => ackAll(h.id)}
                    disabled={busy === `ack-${h.id}`}
                    style={btnSmall}
                  >
                    ล้าง
                  </button>
                </td>
              </tr>
            );
          })}
          {health.length === 0 && (
            <tr><td colSpan={isMobile ? 4 : 6} style={{ ...td, textAlign: 'center', color: '#64748b' }}>
              ยังไม่มีข้อมูล token
            </td></tr>
          )}
        </tbody>
      </table>
      </div>

      {/* Recent Alerts */}
      <h3 style={{ marginTop: 24, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
        📜 ประวัติแจ้งเตือนล่าสุด ({alerts.length})
      </h3>
      <div style={{ maxHeight: 300, overflowY: 'auto', background: '#0f172a', borderRadius: 6 }}>
        {alerts.length === 0 ? (
          <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>ยังไม่มีรายการ</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} style={{
                  borderTop: '1px solid #1e293b',
                  opacity: a.acknowledged ? 0.5 : 1,
                }}>
                  <td style={{ ...td, width: 130, fontSize: 11, color: '#64748b' }}>
                    {new Date(a.created_at).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })}
                  </td>
                  <td style={{ ...td, color: '#f1f5f9' }}>
                    <code style={{ color: '#0ea5e9', marginRight: 6 }}>{a.service}</code>
                    {a.message}
                  </td>
                  <td style={{ ...td, width: 70, fontSize: 11 }}>
                    {a.acknowledged
                      ? <span style={{ color: '#64748b' }}>✓ รับทราบ</span>
                      : <span style={{ color: '#f59e0b' }}>🔔 ใหม่</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' };
const td = { padding: '8px 12px', verticalAlign: 'middle' };
const btnSmall = {
  padding: '3px 10px', fontSize: 11,
  border: '1px solid #334155', background: '#0f172a', color: '#cbd5e1',
  borderRadius: 4, cursor: 'pointer',
};
